<script setup lang="ts">
/**
 * One square, clickable media cell — a sticker, a GIF, a picture to pick.
 * The media (an `<img>`, a `<video>`, or a component that renders one) goes
 * into the default slot and is fitted into the square (`fit`). The cell is a
 * real button: `label` is its accessible name (the media itself is
 * decoration), a click or Enter emits `select`. `selected` makes it a toggle
 * (`aria-pressed`, brand tint).
 *
 * The `#action` slot puts a small control into the top corner — e.g. a delete
 * `NxIconButton` in an edit mode. It sits beside the cell's button, not in
 * it, so it stays a control of its own.
 *
 * Inside an `NxMediaGrid` the cell fills its grid track and joins the grid's
 * arrow-key navigation; on its own it is `size` wide. Attributes (`title`,
 * `data-testid`) go to the button, `class` and `style` to the cell.
 */
import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  useAttrs,
  useTemplateRef,
  type StyleValue,
} from 'vue';
import { mediaGridKey, type NxMediaCellSize } from './context';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** Accessible name of the cell (the sticker's emoji, the GIF's title). */
    label: string;
    disabled?: boolean;
    /** Toggle state: sets `aria-pressed`. Unset leaves the cell a plain button. */
    selected?: boolean;
    /** Square size outside a grid; in a grid the grid's size is the default. */
    size?: NxMediaCellSize;
    /** How the media fills the square: `contain` (whole, letterboxed) or `cover` (cropped). */
    fit?: 'contain' | 'cover';
  }>(),
  { disabled: false, selected: undefined, size: undefined, fit: 'contain' }
);

const emit = defineEmits<{ click: [event: MouseEvent]; select: [] }>();

defineSlots<{
  /** The media: an `<img>`, a `<video>` or a component that renders one. */
  default?(): unknown;
  /** Top corner: a small action on the cell (delete in an edit mode). */
  action?(): unknown;
}>();

const attrs = useAttrs();
const rootStyle = computed(() => attrs.style as StyleValue);
/** Everything but `class` and `style` goes to the button. */
const buttonAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style'))
);

const grid = inject(mediaGridKey, null);
const button = useTemplateRef<HTMLButtonElement>('button');
const size = computed<NxMediaCellSize>(() => props.size ?? grid?.size.value ?? 's');
const tabindex = computed(() => {
  if (!grid || props.disabled) return undefined;
  return grid.tabStop.value === button.value ? 0 : -1;
});

onMounted(() => {
  if (grid && button.value) grid.register(button.value, () => props.disabled);
});
onBeforeUnmount(() => {
  if (grid && button.value) grid.unregister(button.value);
});

function onClick(event: MouseEvent) {
  if (props.disabled) return;
  if (grid && button.value) grid.activate(button.value);
  emit('click', event);
  emit('select');
}

function onFocus() {
  if (grid && button.value) grid.activate(button.value);
}
</script>

<template>
  <div
    class="nx-media-cell"
    :class="[
      `size-${size}`,
      `fit-${props.fit}`,
      {
        'in-grid': !!grid,
        'is-selected': props.selected === true,
        'is-disabled': props.disabled,
      },
      attrs.class,
    ]"
    :style="rootStyle"
  >
    <button
      ref="button"
      type="button"
      class="nx-media-cell__button"
      v-bind="buttonAttrs"
      :disabled="props.disabled"
      :aria-label="props.label"
      :aria-pressed="props.selected === undefined ? undefined : props.selected"
      :tabindex="tabindex"
      @click="onClick"
      @focus="onFocus"
    >
      <span class="nx-media-cell__media" aria-hidden="true">
        <slot />
      </span>
    </button>
    <div v-if="$slots.action" class="nx-media-cell__action">
      <slot name="action" />
    </div>
  </div>
</template>

<style scoped>
.nx-media-cell {
  --media-cell-size: var(--nxt-area-size-xs);

  position: relative;
  display: flex;
  width: var(--media-cell-size);
  min-width: 0;
  aspect-ratio: 1;
}
.nx-media-cell.size-m {
  --media-cell-size: var(--nxt-area-size-sm);
}
/* In a grid the track sets the width; the square follows it. */
.nx-media-cell.in-grid {
  width: auto;
}
.nx-media-cell__button {
  display: grid;
  flex: 1 1 auto;
  place-items: center;
  min-width: 0;
  padding: var(--nxt-space-2xs);
  overflow: hidden;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--nxt-fg-muted);
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.fit-cover .nx-media-cell__button {
  padding: 0;
}
.nx-media-cell__button:hover:not(:disabled) {
  background: var(--nxt-surface-sunken-bg);
}
.nx-media-cell__button:active:not(:disabled) {
  transform: scale(0.97);
}
.nx-media-cell__button:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-media-cell.is-selected .nx-media-cell__button {
  border-color: var(--nxt-brand-border);
  background: var(--nxt-brand-subtle-bg);
}
.nx-media-cell__button:disabled {
  cursor: default;
  opacity: 0.6;
}
.nx-media-cell__media {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: var(--nxt-radius-sm);
  pointer-events: none;
}
.nx-media-cell__media > :slotted(*) {
  max-width: 100%;
  max-height: 100%;
}
.nx-media-cell__media > :slotted(img),
.nx-media-cell__media > :slotted(video) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.fit-cover .nx-media-cell__media > :slotted(img),
.fit-cover .nx-media-cell__media > :slotted(video) {
  object-fit: cover;
}
.nx-media-cell__action {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  display: flex;
}

@media (prefers-reduced-motion: reduce) {
  .nx-media-cell__button {
    transition: none;
  }
  .nx-media-cell__button:active:not(:disabled) {
    transform: none;
  }
}
@media (forced-colors: active) {
  .nx-media-cell.is-selected .nx-media-cell__button {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
